import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const MOBILE_BREAKPOINT = 768;

export const useSidebarDropdown = ({ isExpanded, onDropdownClick }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const [isHovering, setIsHovering] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isPathActive = useCallback((items) => {
        if (!Array.isArray(items)) return false;
        return items.some(subItem => 
            subItem.path === location.pathname || 
            (subItem.items && isPathActive(subItem.items))
        );
    }, [location.pathname]);

    const handleClick = useCallback((e) => {
        if (isMobile) {
            e.preventDefault();
            onDropdownClick();
        }
    }, [isMobile, onDropdownClick]);

    const handleItemClick = useCallback(() => {
        setIsHovering(false);
        onDropdownClick();
    }, [onDropdownClick]);

    const handleMouseEnter = useCallback(() => {
        if (!isMobile && isExpanded) {
            setIsHovering(true);
            onDropdownClick();
        }
    }, [isMobile, isExpanded, onDropdownClick]);

    const handleMouseLeave = useCallback((e) => {
        if (!isMobile && isExpanded) {
            const submenu = e.currentTarget.querySelector('.sidebar-submenu');
            const relatedTarget = e.relatedTarget;
            
            if (!submenu?.contains(relatedTarget) && submenu !== relatedTarget) {
                setIsHovering(false);
                onDropdownClick();
            }
        }
    }, [isMobile, isExpanded, onDropdownClick]);

    const dropdownHandlers = useMemo(() => ({
        handleClick,
        handleItemClick,
        handleMouseEnter,
        handleMouseLeave
    }), [handleClick, handleItemClick, handleMouseEnter, handleMouseLeave]);

    return {
        isMobile,
        isHovering,
        isPathActive,
        ...dropdownHandlers
    };
}; 